import nt from "@nio-fe/tracker-sdk";
import { getAppEnv,humpToLine,getWorkerUserId } from "./utils/index.js";

// 默认配置
const DEFAULT_CONFIG = {
  project: 'nio_business_prod', // 统一采用神策埋点上报和收集，并且所有B端产品共用一个project
}

// 默认参数
const DEFAULT_OPTIONS = {
  platformType: 'web', // 字符串，android、ios，web，mini_program
  language: 'zh-CN', // 区域化的语言，例如nb-NO，en-US
  region: 'CN', // 用户所在地区，例如NB
  appEnv: getAppEnv(), // 当前用户所在环境（dev/test/stg/prod）
  report_source: 'all', // nt,sensors,all
  report_env: 'prod' // dev,test,stg,prod
}

// 标准参数
// 需要转换格式为以 nio_ 开头，并用下划线拼接
const KEY_STANDARD_LIST = ['appId', 'workerUserId', 'language', 'region', 'platformType', 'deviceId']

// 必传参数
const KEY_REQUIRED_LIST = ['appId']

// event所携带参数
const KEY_EVENTS_LIST = ['appId']

/** NIO Track类 */
class NioTracker {
  constructor() {
    this.inited = false // 是否初始化
    this.eventList = ['click']
    this.optionsEvent = {}
  }

  /**
   * 埋点初始化
   * @param {object} options - 公共属性
   * @param {object} initConfig - 初始配置项
   *  @param {string} options.appId
   *  @param {string} options.workerUserId // nio_worker_user_id: 域账号，不带@后缀，例如 san.zhang
   */
  init(options = {}, initConfig = {}) {
    // 参数合并
    const optionsMerged = Object.assign(
      {
        workerUserId: getWorkerUserId(),
      },
      DEFAULT_OPTIONS,
      options
    )

    // 检查必传字段
    for (let i = 0; i < KEY_REQUIRED_LIST.length; i += 1) {
      const keyRequired = KEY_REQUIRED_LIST[i]
      if (!optionsMerged[keyRequired]) {
        throw new Error(`${keyRequired} is required!`)
      }
    }

    // if (!optionsMerged.workerUserId) {
    //   console.warn('workerUserId is recommended in init options!')
    // }

    const projectFina = initConfig.project || DEFAULT_CONFIG.project
    let reportEnvFina = initConfig.report_env
    // 如果没有传入report_env，则根据projectFina判断
    if (!reportEnvFina) {
      if (projectFina === 'nio_business_prod') {
        reportEnvFina = 'prod'
      } else {
        reportEnvFina = DEFAULT_OPTIONS.appEnv || 'test'
      }
    }
    
    nt.init(
      Object.assign(
        initConfig,
        {
          project: initConfig.project || DEFAULT_CONFIG.project,
          // 统一上报到自研埋点的生产环境
          report_env: 'prod',
          report_source: initConfig.report_source || DEFAULT_OPTIONS.report_source,
        }
      )
    )

    // 将参数转为标准形式
    for (let key in optionsMerged) {
      if (KEY_STANDARD_LIST.indexOf(key) > -1) {
        const keyFina = `nio_${humpToLine(key)}`
        optionsMerged[keyFina] = optionsMerged[key]
        if (KEY_EVENTS_LIST.indexOf(key) > -1) {
          this.optionsEvent[keyFina] = optionsMerged[key]
        }
        delete optionsMerged[key]
      }
    }

    if (optionsMerged.loginId) {
      nt.login(optionsMerged.loginId)
      delete optionsMerged['loginId']
    }

    // 注册公共属性
    nt.registerPage(optionsMerged)

    this.inited = true

    // 简单追踪事件批量挂载
    this.multiMountEventSimpleTracker()
  }

  // 简单追踪事件批量挂载
  multiMountEventSimpleTracker() {
    for (let i = 0; i < this.eventList.length; i += 1) {
      const event = this.eventList[i]
      this.mountEventSimpleTracker(event)
    }
  }

  // 简单追踪事件挂载
  mountEventSimpleTracker(event) {
    const eventAttributeName = `nio-tracker-${event}`
    document.addEventListener(event, e => {
      const path = e.composedPath()
      for (let i = 0; i < path.length; i += 1) {
        const item = path[i]
        if (item.getAttribute) {
          const eventKey = item.getAttribute(eventAttributeName)
          if (eventKey) {
            this.track(eventKey)
          }
        }
      }
    })
  }

  /**
   * 埋点事件追踪
   * @param {string} trackName - 埋点事件标识符
   * @param {object} options - 埋点事件参数
   */
  track(trackName, options = {}) {
    if (!this.inited) {
      console.warn('please init first')
    }

    if (!trackName) {
      throw new Error('trackName is required')
    }

    nt.track(trackName, Object.assign({}, this.optionsEvent, options))
  }

  /**
   * 获取SA实例
   * @returns {object}
   */
  getInstance() {
    return nt
  }

  /**
   * 登录
   * @param {string} loginId - 登录ID
   * @param {string} workerUserId - 域账号，不带@后缀，例如 san.zhang
   */
  login({
    loginId,
    workerUserId
  }) {
    try {
      if (loginId) {
        nt.login(loginId)
      } else {
        console.warn('loginId is required', loginId)
      }

      if (workerUserId) {
        nt.registerPage({
          nio_worker_user_id: workerUserId
        })
      }
    } catch (e) {
      console.error('set login error =>', e.message)
    }
  }
}

export default new NioTracker();
